import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { InteractiveMaterialResponseDto } from "@packages/api/madlenAPI.schemas";
import { Text } from "@core/index";
import {
  ContentSection,
  EditButtons,
  SaveCancelButtons,
  useEditState,
  useVideoSection,
  VideoSection,
} from "./utils";

interface DragAndDropItem {
  term: string;
  definition: string;
}

interface DragAndDropTabProps {
  material: InteractiveMaterialResponseDto;
  onDragAndDropChange?: (items: DragAndDropItem[]) => void;
}

const DragAndDropTab = ({
  material,
  onDragAndDropChange,
}: DragAndDropTabProps) => {
  const { t } = useTranslation();
  const itemListRef = useRef<HTMLDivElement>(null);
  const [dragAndDropItems, setDragAndDropItems] = useState<DragAndDropItem[]>(
    (material.dragAndDropItems as DragAndDropItem[]) || [],
  );
  const videoSection = useVideoSection();

  const {
    editingIndex,
    editingItem,
    setEditingItem,
    handleEdit,
    handleDelete,
    handleSave,
    handleCancel,
    handleCreate,
  } = useEditState({
    items: dragAndDropItems,
    onItemsChange: onDragAndDropChange,
    createNewItem: () => ({
      term: "",
      definition: "",
    }),
    setItems: setDragAndDropItems,
    containerRef: itemListRef,
  });

  useEffect(() => {
    setDragAndDropItems((material.dragAndDropItems as DragAndDropItem[]) || []);
  }, [material.dragAndDropItems]);

  return (
    <div className="h-[calc(100vh-20rem)] max-h-[800px] min-h-[600px] flex flex-col lg:flex-row gap-4 relative">
      <VideoSection
        {...videoSection}
        youtubeUrl={material.youtubeUrl}
        generatorMode={material.generatorMode}
      />

      <ContentSection
        title={t("interactive_material.dragAndDrop.title")}
        onAddNew={handleCreate}
        addButtonLabel={t("interactive_material.dragAndDrop.addNew")}
        isEmpty={dragAndDropItems.length === 0}
        emptyStateMessage={t("interactive_material.dragAndDrop.empty")}
        containerRef={itemListRef}
      >
        {dragAndDropItems.map((item, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50 transition-all hover:shadow-md"
          >
            {editingIndex === index && editingItem ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("interactive_material.dragAndDrop.term")}
                  </label>
                  <input
                    type="text"
                    value={editingItem.term}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        term: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors"
                    placeholder={t(
                      "interactive_material.dragAndDrop.termPlaceholder",
                    )}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("interactive_material.dragAndDrop.definition")}
                  </label>
                  <textarea
                    value={editingItem.definition}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        definition: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors resize-none"
                    rows={3}
                    placeholder={t(
                      "interactive_material.dragAndDrop.definitionPlaceholder",
                    )}
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <SaveCancelButtons
                    onSave={() => handleSave(index)}
                    onCancel={handleCancel}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="space-y-2 flex-1 w-full">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <Text variant={"h3"} className="break-all">
                      {item.term}
                    </Text>
                  </div>
                  <Text
                    variant="p"
                    status="subtle"
                    size={"base"}
                    className="break-words"
                  >
                    {item.definition}
                  </Text>
                </div>
                <EditButtons
                  onEdit={() => handleEdit(index)}
                  onDelete={() => handleDelete(index)}
                />
              </div>
            )}
          </div>
        ))}
      </ContentSection>
    </div>
  );
};

export default DragAndDropTab;
